import React from "react";
import { motion } from "framer-motion";
import {
  FaCheckCircle,
  FaCertificate,
  FaExternalLinkAlt,
  FaGraduationCap,
  FaLaptopCode,
} from "react-icons/fa";

const educationData = [
  {
    id: 1,
    icon: <FaLaptopCode />,
    title: "Full Stack Web Developer Bootcamp",
    institution: "Pana Cloud",
    year: "2019-2020",
    description:
      "Intensive full-stack development program covering modern web technologies, cloud platforms, and industry best practices for scalable application development.",
    skills: [
      "React & Modern JavaScript",
      "Vue & Angular",
      "Node.js & Laravel",
      "AWS & Cloud Services",
      "Firebase Integration",
    ],
  },
  {
    id: 2,
    icon: <FaCertificate />,
    title: "Advanced Web Development & Cloud Computing",
    institution: "Professional E-learning Program",
    year: "2021",
    description:
      "Advanced training in modern web development practices, cloud services, and DevOps workflows for enterprise applications.",
    skills: [
      "Modern JavaScript (ES6+)",
      "React Ecosystem",
      "Amazon Web Services",
      "Cloud Architecture",
      "CI/CD Practices",
    ],
  },
  {
    id: 3,
    icon: <FaGraduationCap />,
    title: "Enterprise Networking",
    institution: "Oslo and Akershus University College",
    year: "2014",
    description:
      "Advanced Single Subject Master Level Course in Enterprise Networking: Practices and Technologies, covering cloud infrastructure and virtualization.",
    skills: [
      "Cloud Based Network",
      "VMWare",
      "Advanced Virtual Box Labs",
      "System Infrastructure",
    ],
  },
  {
    id: 4,
    icon: <FaCertificate />,
    title: "Network Design And Management",
    institution: "Halmstad University, Sweden",
    year: "2012",
    description:
      "Diploma in Network Design And Management (30 ECTS) with focus on system administration, security, and infrastructure management.",
    skills: [
      "System Administration",
      "Network Security",
      "Linux Administration",
      "Infrastructure Management",
    ],
  },
  {
    id: 5,
    icon: <FaGraduationCap />,
    title: "MSC Electrical Engineering (Telecommunication)",
    institution: "Blekinge Institute of Technology, Karlskrona",
    year: "2010",
    description:
      "Master of Science in Electrical Engineering with specialization in Telecommunication systems, providing strong foundation in digital systems and software engineering (90 ECTS).",
    skills: [
      "Digital Systems",
      "TCP/IP & Protocols",
      "Software Engineering",
      "System Architecture",
      "Complex Analysis",
    ],
  },
  {
    id: 6,
    icon: <FaGraduationCap />,
    title: "BS Telecommunication System",
    institution: "B.Z University, Multan Pakistan",
    year: "2008",
    description:
      "Bachelor of Science degree providing comprehensive foundation in computer systems, data communication, and software development fundamentals.",
    skills: [
      "Data Communication",
      "Computer Systems",
      "Software Development",
      "System Design",
      "Digital Communications",
    ],
  },
];

const caseStudies = [
  {
    id: 1,
    icon: <FaLaptopCode />,
    title: "React Native Design System (Case Study)",
    year: "2025-2026",
    institution: "Nordlys Mobile Platform",
    summary:
      "Defined and productionized a reusable React Native component library with strict TypeScript APIs, stronger accessibility defaults, unified form/error behavior, and a default-theme-first customization model for predictable adoption.",
    highlights: [
      "Simplified theming strategy to one production-ready default theme, with safe custom overrides validated through theme guardrails.",
      "Standardized Select UX with ActionSheet patterns and aligned input/select/validation behavior across components.",
      "Reduced package footprint via subpath exports, cleaned publish artifacts, and removed unused code paths.",
    ],
    skills: [
      "React Native",
      "TypeScript",
      "Design Tokens",
      "Accessibility",
      "Expo Test App",
    ],
    links: [
      {
        label: "Design System Repo",
        href: "https://github.com/juac08/AwesomeStorybook",
      },
      {
        label: "Validation App Demo",
        href: "/images/flow.mp4",
      },
    ],
  },
];

const Profile = () => {
  return (
    <motion.div
      exit={{ opacity: 0 }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <section className="section education-section">
        <div className="section-title">
          <h2>Educational Background</h2>
          <div className="underline"></div>
        </div>

        <div className="education-timeline">
          {educationData.map((item, index) => (
            <motion.article
              key={item.id}
              className="education-item"
              data-aos="fade-up"
              data-aos-delay={index * 100}
            >
              <div className="education-icon-wrapper">
                <div className="education-icon">{item.icon}</div>
              </div>

              <div className="education-content">
                <div className="education-header">
                  <h3>{item.title}</h3>
                  <span className="education-year">{item.year}</span>
                </div>

                <h4 className="education-institution">{item.institution}</h4>

                <p className="education-description">{item.description}</p>

                <div className="education-skills">
                  {item.skills.map((skill, idx) => (
                    <span key={idx} className="education-skill-tag">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="section education-section profile-case-study-section">
        <div className="section-title">
          <h2>Case Studies</h2>
          <div className="underline"></div>
        </div>

        <div className="education-timeline">
          {caseStudies.map((item, index) => (
            <motion.article
              key={item.id}
              className="education-item"
              data-aos="fade-up"
              data-aos-delay={index * 100}
            >
              <div className="education-icon-wrapper">
                <div className="education-icon">{item.icon}</div>
              </div>

              <div className="education-content">
                <div className="education-header">
                  <h3>{item.title}</h3>
                  <span className="education-year">{item.year}</span>
                </div>

                <h4 className="education-institution">{item.institution}</h4>

                <p className="education-description">{item.summary}</p>

                <div className="experience-duties-list">
                  {item.highlights.map((highlight, idx) => (
                    <div key={idx} className="duty-item">
                      <FaCheckCircle className="duty-check-icon" />
                      <p>{highlight}</p>
                    </div>
                  ))}
                </div>

                <div className="education-skills">
                  {item.skills.map((skill, idx) => (
                    <span key={idx} className="education-skill-tag">
                      {skill}
                    </span>
                  ))}
                </div>

                <div className="case-study-links">
                  {item.links.map((link) => (
                    <a
                      key={link.label}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="case-study-link"
                    >
                      <span>{link.label}</span>
                      <FaExternalLinkAlt />
                    </a>
                  ))}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>
    </motion.div>
  );
};

export default Profile;
